// League workspace — Announcements view.
//
// Committee-facing subset of the unified announcements board — every
// row we render has isLeagueOnly = true. Post form auto-sets the same
// flag so there's no audience picker to fiddle with.
//
// Uses the same /announcements API as Gabruz Universe. No new backend
// needed.

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, Pencil, Trash2, X, Pin, Megaphone } from "lucide-react";
import { Panel } from "../../components/primitives";
import { useToast } from "../../components/Toast";
import Avatar from "../../components/Avatar";
import { api } from "../../api";
import { canDo } from "../../data";
import { fmtInstantEastern } from "../../utils/time";

const PRIORITIES = ["Normal", "Important", "Urgent"];
const EMPTY = { title: "", body: "", priority: "Normal", pinned: false };

export default function LeagueAnnouncements({ user }) {
  const toast = useToast();
  const [items,     setItems]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [busy,      setBusy]      = useState(false);

  const [showForm,  setShowForm]  = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form,      setForm]      = useState(EMPTY);

  const canCreate = canDo(user, "announcements", "create");
  const canUpdate = canDo(user, "announcements", "update");
  const canDelete = canDo(user, "announcements", "delete");

  async function refresh() {
    setLoading(true);
    try {
      const all = await api.listAnnouncements();
      setItems(all.filter(a => a.audience === "League" || a.isLeagueOnly));
    } catch (err) {
      toast.error(err.message || "Couldn't load announcements.");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!showForm) return;
    const onKey = e => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showForm]);

  function openCreate() { setEditingId(null); setForm(EMPTY); setShowForm(true); }
  function openEdit(a) {
    setEditingId(a.id);
    setForm({ title: a.title, body: a.body, priority: a.priority || "Normal", pinned: !!a.pinned });
    setShowForm(true);
  }
  function close() { setShowForm(false); setEditingId(null); setForm(EMPTY); }

  async function submit(e) {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and body are required."); return;
    }
    setBusy(true);
    try {
      const payload = {
        title:        form.title.trim(),
        body:         form.body.trim(),
        priority:     form.priority,
        pinned:       form.pinned,
        isLeagueOnly: true,
        groupId:      null,
        teamId:       null
      };
      if (editingId) {
        await api.updateAnnouncement(editingId, payload);
        toast.success("Announcement updated.");
      } else {
        await api.createAnnouncement(payload);
        toast.success("Announcement posted.");
      }
      close();
      await refresh();
    } catch (err) { toast.error(err.message || "Save failed."); }
    finally { setBusy(false); }
  }

  async function remove(a) {
    if (!confirm(`Delete announcement "${a.title}"?`)) return;
    try {
      await api.deleteAnnouncement(a.id);
      await refresh();
    } catch (err) { toast.error("Delete failed: " + err.message); }
  }

  return (
    <div className="stack league-view">
      <div className="builderTop">
        <div>
          <p>Pittsburgh Cricket League</p>
          <h2>League Announcements</h2>
        </div>
        <div>
          {canCreate && (
            <button className="btn" onClick={openCreate}>
              <Plus size={16}/> New Announcement
            </button>
          )}
        </div>
      </div>

      <Panel title={
        <div style={{display:"flex", alignItems:"center", gap:8, flex:1}}>
          <Megaphone size={18}/>
          <span>Announcement Board</span>
          <em style={{marginLeft:"auto", color:"var(--muted)", fontStyle:"normal", fontSize:12}}>
            {items.length} posted
          </em>
        </div>
      }>
        {loading && <p style={{color:"var(--muted)"}}>Loading…</p>}
        {!loading && items.length === 0 && (
          <div className="league-annEmpty">
            <Megaphone size={28}/>
            <p>Nothing posted yet. Committee announcements you post here are visible only to committee-tier members.</p>
          </div>
        )}
        <div className="league-annList">
          {items.map(a => (
            <article
              key={a.id}
              className={`league-annCard league-annCard--${(a.priority || "normal").toLowerCase()}${a.pinned ? " league-annCard--pinned" : ""}`}
            >
              <header className="league-annCard-head">
                <div className="league-annCard-headLeft">
                  {a.pinned && (
                    <span className="league-annCard-pinPill" title="Pinned">
                      <Pin size={11}/> Pinned
                    </span>
                  )}
                  <span className={`league-annCard-priorityPill league-annCard-priorityPill--${(a.priority || "normal").toLowerCase()}`}>
                    {a.priority || "Normal"}
                  </span>
                </div>
                {(canUpdate || canDelete) && (
                  <div className="league-annCard-actions">
                    {canUpdate && (
                      <button type="button" className="league-annCard-iconBtn"
                        onClick={() => openEdit(a)} title="Edit">
                        <Pencil size={14}/>
                      </button>
                    )}
                    {canDelete && (
                      <button type="button" className="league-annCard-iconBtn league-annCard-iconBtn--danger"
                        onClick={() => remove(a)} title="Delete">
                        <Trash2 size={14}/>
                      </button>
                    )}
                  </div>
                )}
              </header>
              <h3 className="league-annCard-title">{a.title}</h3>
              <p className="league-annCard-body">{a.body}</p>
              <footer className="league-annCard-foot">
                <Avatar member={{ id: a.postedById, name: a.postedBy }} size={22}/>
                <span>Posted by <b>{a.postedBy}</b></span>
                <span className="league-annCard-when">· {fmtInstantEastern(a.createdAt)}</span>
              </footer>
            </article>
          ))}
        </div>
      </Panel>

      {showForm && createPortal((
        <div className="modal" onClick={close}>
          <div className="modalInner" onClick={e => e.stopPropagation()}>
            <button type="button" className="close" onClick={close} aria-label="Close">
              <X size={20}/>
            </button>
            <Panel title={editingId ? "Edit League Announcement" : "Post League Announcement"}>
              <form className="form" onSubmit={submit}>
                <div className="two">
                  <div>
                    <label>Priority</label>
                    <select value={form.priority}
                      onChange={e => setForm({ ...form, priority: e.target.value })}>
                      {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label>Pin to top?</label>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 8, textTransform: "none", letterSpacing: 0, color: "var(--text)", padding: "10px 0" }}>
                      <input type="checkbox" checked={form.pinned}
                        onChange={e => setForm({ ...form, pinned: e.target.checked })}
                        style={{ width: "auto" }}/>
                      <Pin size={14}/> Show this at the top
                    </label>
                  </div>
                </div>

                <label>Title *</label>
                <input value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Registration deadline extended to July 15"/>

                <label>Body *</label>
                <textarea rows={5} value={form.body}
                  onChange={e => setForm({ ...form, body: e.target.value })}
                  placeholder="Full announcement text visible to every committee member."/>

                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button type="button" className="dark" onClick={close} disabled={busy}>Cancel</button>
                  <button type="submit" className="btn" disabled={busy}>
                    {busy ? "Saving…" : editingId ? "Save Changes" : "Post Announcement"}
                  </button>
                </div>
              </form>
            </Panel>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
